/**
 * ProcessingCanvasView - Expanded "behind the curtains" visualization
 *
 * A full-screen canvas-style view showing detailed processing progress:
 * - Timeline of document processing stages
 * - Live document cards with extraction status
 * - Entity discovery stream
 * - Technical metrics and LLM activity
 */

import { useState, useEffect, useMemo } from "react";
import {
  X,
  FileText,
  Building2,
  CreditCard,
  Users,
  Check,
  Loader2,
  AlertTriangle,
  Zap,
  Brain,
  Database,
  Clock,
  Hash,
  FileSpreadsheet,
  FileType,
  Scan,
  Sparkles,
  ArrowRight,
  ChevronRight,
  Activity,
  Gauge,
  Server,
  Code2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { SourceDocument } from "@shared/discovery-types";

// ============================================================================
// TYPES
// ============================================================================

interface ProcessingStage {
  id: string;
  label: string;
  description: string;
  status: "pending" | "active" | "completed" | "error";
  startTime?: number;
  endTime?: number;
  details?: string[];
}

interface DocumentProcessingState {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  stage:
    | "queued"
    | "parsing"
    | "classifying"
    | "extracting"
    | "complete"
    | "error";
  progress: number;
  classification?: string;
  extractedItems?: {
    entities: number;
    dates: number;
    amounts: number;
    ibans: number;
  };
  error?: string;
}

interface EntityDiscovery {
  id: string;
  type: "vendor" | "customer" | "bank" | "company";
  name: string;
  context: string;
  confidence: number;
  timestamp: number;
}

interface MetricsState {
  tokensUsed: number;
  llmCalls: number;
  processingTimeMs: number;
  documentsProcessed: number;
  totalDocuments: number;
  entitiesFound: number;
  stage: string;
}

interface ProcessingCanvasViewProps {
  /** Whether the canvas is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Number of files being processed */
  fileCount: number;
  /** Existing documents with their current status (if available) */
  existingDocuments?: SourceDocument[];
  /** Display name of the AI model being used */
  modelName?: string;
}

// ============================================================================
// SIMULATED DATA (would be replaced with real WebSocket/polling data)
// ============================================================================

const PROCESSING_STAGES: ProcessingStage[] = [
  {
    id: "intake",
    label: "Document Intake",
    description: "Parsing raw files into processable documents",
    status: "pending",
    details: [
      "Detecting file types (PDF, Excel, CSV)",
      "Extracting raw text content",
      "Handling Swiss character encoding",
    ],
  },
  {
    id: "census",
    label: "Census (Map Phase)",
    description: "Parallel analysis of each document",
    status: "pending",
    details: [
      "Running Scout agents on each document",
      "Identifying document types with confidence scores",
      "Extracting entities, dates, and identifiers",
      "Building per-document metadata reports",
    ],
  },
  {
    id: "synthesis",
    label: "Synthesis (Reduce Phase)",
    description: "Combining insights into unified understanding",
    status: "pending",
    details: [
      "Merging entity mentions across documents",
      "Resolving conflicting information",
      "Building business hypothesis",
      "Identifying data gaps",
    ],
  },
  {
    id: "holistic",
    label: "Holistic Extraction",
    description: "Context-aware transaction processing",
    status: "pending",
    details: [
      "Building reference model of business",
      "Parsing transactions with context",
      "Cleaning entity names (deduplication)",
      "Categorizing vendors and customers",
    ],
  },
  {
    id: "finalize",
    label: "Finalization",
    description: "Preparing preview for review",
    status: "pending",
    details: [
      "Calculating confidence scores",
      "Detecting data conflicts",
      "Generating assumptions",
      "Building import preview",
    ],
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ProcessingCanvasView({
  isOpen,
  onClose,
  fileCount,
  existingDocuments = [],
  modelName,
}: ProcessingCanvasViewProps) {
  // Simulated state (would be real data in production)
  const [stages, setStages] = useState<ProcessingStage[]>(PROCESSING_STAGES);
  const [documents, setDocuments] = useState<DocumentProcessingState[]>([]);
  const [entities, setEntities] = useState<EntityDiscovery[]>([]);
  const [metrics, setMetrics] = useState<MetricsState>({
    tokensUsed: 0,
    llmCalls: 0,
    processingTimeMs: 0,
    documentsProcessed: 0,
    totalDocuments: fileCount,
    entitiesFound: 0,
    stage: "Starting...",
  });
  const [logEntries, setLogEntries] = useState<string[]>([]);
  const [startTime] = useState(Date.now());

  // Initialize documents from props
  useEffect(() => {
    if (existingDocuments.length > 0) {
      setDocuments(
        existingDocuments.map(doc => ({
          id: doc.id,
          name: doc.name,
          mimeType: doc.mimeType,
          size: doc.size,
          stage: doc.processingStatus === "completed" ? "complete" : "queued",
          progress: doc.processingStatus === "completed" ? 100 : 0,
          classification: doc.classification?.type,
        }))
      );
    } else if (fileCount > 0) {
      // Create placeholder documents
      setDocuments(
        Array.from({ length: fileCount }, (_, i) => ({
          id: `doc-${i}`,
          name: `Document ${i + 1}`,
          mimeType: "unknown",
          size: 0,
          stage: "queued" as const,
          progress: 0,
        }))
      );
    }
  }, [existingDocuments, fileCount]);

  // Simulate processing progress
  useEffect(() => {
    if (!isOpen) return;

    const stageInterval = setInterval(() => {
      setStages(prev => {
        const currentIndex = prev.findIndex(s => s.status === "active");
        const nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;

        return prev.map((stage, i) => {
          if (i < nextIndex) {
            return {
              ...stage,
              status: "completed" as const,
              endTime: Date.now(),
            };
          }
          if (i === nextIndex && i < prev.length) {
            return {
              ...stage,
              status: "active" as const,
              startTime: Date.now(),
            };
          }
          return stage;
        });
      });

      setMetrics(prev => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / 60000) * 100, 95);
        return {
          ...prev,
          tokensUsed: Math.floor(progress * 150),
          llmCalls: Math.floor(progress / 10),
          processingTimeMs: elapsed,
          documentsProcessed: Math.min(Math.floor(progress / 15), fileCount),
          entitiesFound: Math.floor(progress * 0.5),
          stage:
            stages.find(s => s.status === "active")?.label || "Processing...",
        };
      });
    }, 4000);

    const docInterval = setInterval(() => {
      setDocuments(prev =>
        prev.map(doc => {
          if (doc.stage === "complete" || doc.stage === "error") return doc;

          const stages: DocumentProcessingState["stage"][] = [
            "queued",
            "parsing",
            "classifying",
            "extracting",
            "complete",
          ];
          const currentIdx = stages.indexOf(doc.stage);
          const nextIdx = Math.min(currentIdx + 1, stages.length - 1);

          return {
            ...doc,
            stage: stages[nextIdx],
            progress: Math.min(doc.progress + 25, 100),
            extractedItems:
              nextIdx >= 3
                ? {
                    entities: Math.floor(Math.random() * 10) + 1,
                    dates: Math.floor(Math.random() * 20) + 5,
                    amounts: Math.floor(Math.random() * 50) + 10,
                    ibans: Math.floor(Math.random() * 3),
                  }
                : undefined,
          };
        })
      );
    }, 2500);

    const entityInterval = setInterval(() => {
      const entityTypes: EntityDiscovery["type"][] = [
        "vendor",
        "customer",
        "bank",
        "company",
      ];
      const sampleNames = {
        vendor: [
          "Swisscom AG",
          "SBB",
          "Migros",
          "Sunrise",
          "Salt Mobile",
          "PostFinance",
        ],
        customer: [
          "Müller GmbH",
          "Tech Solutions AG",
          "Alpine Services",
          "Swiss Consulting",
        ],
        bank: ["UBS", "Credit Suisse", "Raiffeisen", "ZKB", "PostFinance"],
        company: ["Habitusnet Consulting AG", "Sample Corp GmbH"],
      };

      const type = entityTypes[Math.floor(Math.random() * entityTypes.length)];
      const names = sampleNames[type];
      const name = names[Math.floor(Math.random() * names.length)];

      const newEntity: EntityDiscovery = {
        id: `entity-${Date.now()}`,
        type,
        name,
        context: `Found in document analysis`,
        confidence: 70 + Math.floor(Math.random() * 30),
        timestamp: Date.now(),
      };

      setEntities(prev => [newEntity, ...prev].slice(0, 20));
    }, 1500);

    const logInterval = setInterval(() => {
      const logMessages = [
        "[Scout] Analyzing document structure...",
        "[Census] Extracting IBAN: CH** **** **** ****",
        "[General] Merging entity mentions...",
        "[Holistic] Building reference model...",
        "[Scout] Document type: bank_statement (85% confidence)",
        "[Census] Found 15 transaction dates",
        "[General] Resolving company name variants...",
        "[Holistic] Processing vendor transactions...",
        "[Scout] Extracting Swiss UID...",
        "[Census] Detected currency: CHF",
      ];

      const newLog =
        logMessages[Math.floor(Math.random() * logMessages.length)];
      setLogEntries(prev =>
        [...prev, `${new Date().toLocaleTimeString()} ${newLog}`].slice(-50)
      );
    }, 800);

    return () => {
      clearInterval(stageInterval);
      clearInterval(docInterval);
      clearInterval(entityInterval);
      clearInterval(logInterval);
    };
  }, [isOpen, fileCount, startTime]);

  const activeStage = useMemo(
    () => stages.find(s => s.status === "active"),
    [stages]
  );
  const completedCount = useMemo(
    () => stages.filter(s => s.status === "completed").length,
    [stages]
  );
  const overallProgress = Math.round((completedCount / stages.length) * 100);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary animate-pulse" />
            <h2 className="text-xl font-semibold">Discovery Engine</h2>
          </div>
          <Badge variant="outline" className="gap-1">
            <Activity className="h-3 w-3" />
            Processing {fileCount} files
          </Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4 p-4 h-[calc(100vh-73px)] overflow-hidden">
        {/* Left Column: Pipeline Stages */}
        <div className="col-span-3 space-y-4">
          <Card className="h-full flex flex-col">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Processing Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto py-0">
              <div className="space-y-1">
                {stages.map((stage, index) => (
                  <div key={stage.id}>
                    <div
                      className={cn(
                        "p-3 rounded-lg transition-all",
                        stage.status === "active" &&
                          "bg-primary/10 border border-primary/30",
                        stage.status === "completed" && "bg-[var(--success)]/5",
                        stage.status === "pending" && "opacity-50"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={cn(
                            "mt-0.5 flex-shrink-0 h-5 w-5 rounded-full flex items-center justify-center",
                            stage.status === "completed" &&
                              "bg-[var(--success)] text-[var(--success-foreground)]",
                            stage.status === "active" &&
                              "bg-primary text-primary-foreground",
                            stage.status === "pending" && "bg-muted"
                          )}
                        >
                          {stage.status === "completed" ? (
                            <Check className="h-3 w-3" />
                          ) : stage.status === "active" ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <span className="text-[10px]">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {stage.label}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stage.description}
                          </div>

                          {stage.status === "active" && stage.details && (
                            <div className="mt-2 space-y-1">
                              {stage.details.map((detail, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                                >
                                  <ChevronRight className="h-3 w-3 text-primary" />
                                  <span>{detail}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {index < stages.length - 1 && (
                      <div className="flex justify-center py-1">
                        <ArrowRight
                          className={cn(
                            "h-4 w-4 rotate-90",
                            stage.status === "completed"
                              ? "text-[var(--success)]"
                              : "text-muted-foreground/30"
                          )}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Center Column: Documents & Entities */}
        <div className="col-span-6 space-y-4 flex flex-col">
          {/* Document Processing Grid */}
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Document Processing
                </span>
                <Badge variant="secondary">
                  {documents.filter(d => d.stage === "complete").length}/
                  {documents.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto py-0">
              <div className="grid grid-cols-2 gap-2">
                {documents.map(doc => (
                  <DocumentCard key={doc.id} document={doc} />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Entity Discovery Stream */}
          <Card className="h-64 flex flex-col">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Entity Discovery Stream
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto py-0">
              <ScrollArea className="h-full">
                <div className="space-y-1">
                  {entities.map(entity => (
                    <EntityCard key={entity.id} entity={entity} />
                  ))}
                  {entities.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-4">
                      Waiting for entities...
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Metrics & Logs */}
        <div className="col-span-3 space-y-4 flex flex-col">
          {/* Metrics Dashboard */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Gauge className="h-4 w-4 text-primary" />
                Live Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-0 pb-4">
              {/* Progress */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">
                    Overall Progress
                  </span>
                  <span className="font-medium">{overallProgress}%</span>
                </div>
                <Progress value={overallProgress} className="h-2" />
              </div>

              <Separator />

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <MetricItem
                  icon={Clock}
                  label="Time Elapsed"
                  value={formatDuration(metrics.processingTimeMs)}
                />
                <MetricItem
                  icon={Hash}
                  label="LLM Calls"
                  value={metrics.llmCalls.toString()}
                />
                <MetricItem
                  icon={Database}
                  label="Tokens Used"
                  value={formatNumber(metrics.tokensUsed)}
                />
                <MetricItem
                  icon={FileText}
                  label="Docs Processed"
                  value={`${metrics.documentsProcessed}/${metrics.totalDocuments}`}
                />
                <MetricItem
                  icon={Users}
                  label="Entities Found"
                  value={metrics.entitiesFound.toString()}
                />
                <MetricItem
                  icon={Server}
                  label="Current Stage"
                  value={activeStage?.label.split(" ")[0] || "—"}
                />
              </div>

              {/* Tech Stack */}
              <Separator />
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Technology Stack
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs">
                    {modelName ?? "Gemini AI"}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Map-Reduce
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Swiss Parsers
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Live Log */}
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Code2 className="h-4 w-4 text-primary" />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden py-0">
              <ScrollArea className="h-full font-mono text-[10px]">
                <div className="space-y-0.5 pb-4">
                  {logEntries.map((entry, i) => (
                    <div
                      key={i}
                      className={cn(
                        "text-muted-foreground leading-tight",
                        i === logEntries.length - 1 &&
                          "text-foreground font-medium"
                      )}
                    >
                      {entry}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function DocumentCard({ document }: { document: DocumentProcessingState }) {
  const stageIcons: Record<DocumentProcessingState["stage"], React.ReactNode> =
    {
      queued: <Clock className="h-3 w-3" />,
      parsing: <FileType className="h-3 w-3" />,
      classifying: <Scan className="h-3 w-3" />,
      extracting: <Sparkles className="h-3 w-3" />,
      complete: <Check className="h-3 w-3" />,
      error: <AlertTriangle className="h-3 w-3" />,
    };

  const stageLabels: Record<DocumentProcessingState["stage"], string> = {
    queued: "Queued",
    parsing: "Parsing",
    classifying: "Classifying",
    extracting: "Extracting",
    complete: "Complete",
    error: "Error",
  };

  const getFileIcon = (mimeType: string, name: string) => {
    if (
      mimeType.includes("spreadsheet") ||
      name.endsWith(".xlsx") ||
      name.endsWith(".xls")
    ) {
      return <FileSpreadsheet className="h-4 w-4 text-[var(--success)]" />;
    }
    if (mimeType === "application/pdf" || name.endsWith(".pdf")) {
      return <FileText className="h-4 w-4 text-destructive" />;
    }
    if (mimeType === "text/csv" || name.endsWith(".csv")) {
      return <FileText className="h-4 w-4 text-primary" />;
    }
    return <FileText className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div
      className={cn(
        "p-2 rounded-lg border transition-all",
        document.stage === "complete" &&
          "border-[var(--success)]/30 bg-[var(--success)]/5",
        document.stage === "error" && "border-destructive/30 bg-destructive/5",
        ["parsing", "classifying", "extracting"].includes(document.stage) &&
          "border-primary/30 bg-primary/5"
      )}
    >
      <div className="flex items-start gap-2">
        {getFileIcon(document.mimeType, document.name)}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate" title={document.name}>
            {document.name}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px]",
                document.stage === "complete" && "text-[var(--success)]",
                document.stage === "error" && "text-destructive",
                ["parsing", "classifying", "extracting"].includes(
                  document.stage
                ) && "text-primary"
              )}
            >
              {stageIcons[document.stage]}
              {stageLabels[document.stage]}
            </span>
            {document.classification && (
              <Badge variant="secondary" className="text-[9px] px-1 py-0">
                {document.classification.replace(/_/g, " ")}
              </Badge>
            )}
          </div>

          {document.extractedItems && (
            <div className="flex gap-2 mt-1 text-[9px] text-muted-foreground">
              <span>{document.extractedItems.entities} entities</span>
              <span>{document.extractedItems.dates} dates</span>
              <span>{document.extractedItems.amounts} amounts</span>
            </div>
          )}
        </div>
      </div>

      {document.stage !== "complete" && document.stage !== "error" && (
        <Progress value={document.progress} className="h-1 mt-2" />
      )}
    </div>
  );
}

function EntityCard({ entity }: { entity: EntityDiscovery }) {
  const typeIcons: Record<EntityDiscovery["type"], React.ReactNode> = {
    vendor: <Users className="h-3 w-3 text-[var(--warning)]" />,
    customer: <Users className="h-3 w-3 text-[var(--success)]" />,
    bank: <CreditCard className="h-3 w-3 text-primary" />,
    company: <Building2 className="h-3 w-3 text-primary" />,
  };

  const typeColors: Record<EntityDiscovery["type"], string> = {
    vendor: "bg-[var(--warning)]/10 border-[var(--warning)]/20",
    customer: "bg-[var(--success)]/10 border-[var(--success)]/20",
    bank: "bg-primary/10 border-primary/20",
    company: "bg-primary/10 border-primary/20",
  };

  return (
    <div className={cn("p-2 rounded border text-xs", typeColors[entity.type])}>
      <div className="flex items-center gap-2">
        {typeIcons[entity.type]}
        <span className="font-medium flex-1 truncate">{entity.name}</span>
        <Badge variant="outline" className="text-[9px] px-1">
          {entity.confidence}%
        </Badge>
      </div>
    </div>
  );
}

function MetricItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function formatNumber(num: number): string {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
}
